import { useMemo } from "react";
import { useAppSelector } from "../../redux/store/hooks";
import {
  useGetBrokersQuery,
  useGetOrganizationsQuery,
  useGetSubscriptionsQuery,
} from "../../redux/api";
import { StatCard } from "../../components/StatCard";
import { IconBuilding, IconChart, IconReport, IconShield } from "../../components/icons";

const money = (v: number) => v.toLocaleString();

// Broker → Dashboard. The signed-in broker's own performance: organizations
// they referred and the commission earned on those subscriptions. The broker
// is matched to the session user by email.
const BrokerDashboard = () => {
  const email = useAppSelector((s) => s.layout.user.email);

  const { data: brokers = [] } = useGetBrokersQuery();
  const { data: organizations = [] } = useGetOrganizationsQuery();
  const { data: subscriptions = [] } = useGetSubscriptionsQuery();

  const broker = brokers.find(
    (b) => (b.email ?? "").toLowerCase() === email.toLowerCase(),
  );

  const stats = useMemo(() => {
    if (!broker)
      return { referred: 0, revenue: 0, commission: 0, rate: 0 };

    const referred = organizations.filter((o) => o.brokerId === broker.id);
    const referredIds = new Set(referred.map((o) => o.id));
    const revenue = subscriptions
      .filter((s) => referredIds.has(s.organizationId))
      .reduce((s, x) => s + (x.amountPaid ?? 0), 0);
    const rate = broker.commissionRate ?? 0;
    return {
      referred: referred.length,
      revenue,
      commission: (revenue * rate) / 100,
      rate,
    };
  }, [broker, organizations, subscriptions]);

  return (
    <div className="dashboard">
      <div className="dashboard-banner">
        <div>
          <h1>Broker Dashboard</h1>
        </div>
      </div>

      {!broker ? (
        <section className="panel">
          <div className="profile-empty">
            No broker account is linked to {email}. Ask the platform owner to
            create a broker with this email.
          </div>
        </section>
      ) : (
        <div className="stat-grid">
          <StatCard
            label="Referred organizations"
            value={stats.referred}
            tone="primary"
            icon={<IconBuilding />}
          />
          <StatCard
            label="Referred revenue"
            value={money(stats.revenue)}
            tone="success"
            icon={<IconReport />}
          />
          <StatCard
            label="Your commission"
            value={money(Math.round(stats.commission))}
            hint={`${stats.rate}% of referred revenue`}
            tone="success"
            icon={<IconChart />}
          />
          <StatCard
            label="Commission rate"
            value={`${stats.rate}%`}
            tone="warning"
            icon={<IconShield />}
          />
        </div>
      )}
    </div>
  );
};

export default BrokerDashboard;
